// MOVE selection — translate every cell in the current selection by an
// integer (dx, dy, dz) offset, driven by where the controller's LASER
// is pointing rather than only where the controller is positioned.
//
// Press requires the laser to be on a voxel that's currently selected.
// The hit point becomes the "grab point"; we capture the world-space
// grab distance from the controller. Each frame the new endpoint =
// controllerPos + controllerDir * grabDistance — so rotating the
// controller swings the selection (laser sweep at fixed distance), and
// translating the controller pushes/pulls it (endpoint follows).
//
// Lift-then-place semantics: originals are lifted from the grid on
// press, placed at the current offset every frame. Non-selection cells
// under destinations get "displaced" — stashed in `displaced` Map and
// restored on offset change (e.g. user drags back). On release, the
// Selection updates to the new positions.

import * as THREE from "three";
import { controllerStates } from "../../input/controllers";
import {
  CELL_SIZE,
  type PaletteIndex,
  type VoxelGrid,
} from "../../model/voxel-grid";
import type { Selection } from "../../model/selection";
import { faceNormal } from "../../model/voxel-pick";
import { laserEndpointInGridLocal } from "../laser-endpoint";
import type { MirrorConfig } from "../mirror";
import type { CellBounds, PickTarget } from "../pick";
import { interaction, setInteraction } from "../state";
import type { Interaction } from "../types";

type MoveState = Extract<Interaction, { kind: "move_selection" }>;

const _scratchEndpointGrid = new THREE.Vector3();

// Start a move. Returns true if the press resolved to a selected voxel
// and the lift succeeded; false otherwise (caller can use this for
// haptic feedback or to skip state changes).
export function startMoveSelection(
  pick: PickTarget,
  controllerIndex: number,
  grid: VoxelGrid,
  gridGroup: THREE.Object3D,
  selection: Selection,
  screenDriven: boolean,
  mirrorCfg: MirrorConfig | null = null,
): boolean {
  if (!pick || pick.kind !== "voxel") return false;
  if (!selection.has(pick.ix, pick.iy, pick.iz)) return false;
  if (selection.size() === 0) return false;

  // Snapshot every selected cell's current palette + the bounding box
  // (used per-frame to clamp the drag offset against the volume walls).
  const originals: Array<{
    ix: number;
    iy: number;
    iz: number;
    palette: PaletteIndex;
  }> = [];
  let minIx = Infinity;
  let maxIx = -Infinity;
  let minIy = Infinity;
  let maxIy = -Infinity;
  let minIz = Infinity;
  let maxIz = -Infinity;
  for (const c of selection.entries()) {
    const palette = grid.get(c.ix, c.iy, c.iz);
    if (palette === 0) continue;
    originals.push({ ix: c.ix, iy: c.iy, iz: c.iz, palette });
    if (c.ix < minIx) minIx = c.ix;
    if (c.ix > maxIx) maxIx = c.ix;
    if (c.iy < minIy) minIy = c.iy;
    if (c.iy > maxIy) maxIy = c.iy;
    if (c.iz < minIz) minIz = c.iz;
    if (c.iz > maxIz) maxIz = c.iz;
  }
  if (originals.length === 0) return false;
  const originalsBBox = { minIx, maxIx, minIy, maxIy, minIz, maxIz };

  // Capture grab point in grid-local from the press-time laser endpoint.
  // pick.distance is already the world-space face-plane distance — use
  // it directly so the grab anchor matches what the user clicked on.
  const startGrabPosGrid = new THREE.Vector3();
  laserEndpointInGridLocal(
    controllerIndex,
    pick.distance,
    gridGroup,
    startGrabPosGrid,
  );
  // Lift originals from the grid.
  for (const o of originals) grid.delete(o.ix, o.iy, o.iz);

  const [nx, ny] = faceNormal(pick.face);
  const faceNormalAxis: 0 | 1 | 2 = nx !== 0 ? 0 : ny !== 0 ? 1 : 2;

  // Mirror move: capture the plane centres + which side the grabbed cell is on,
  // so the grabbed half follows the cursor and the other half mirrors it.
  const mirrorOn =
    mirrorCfg !== null && (mirrorCfg.x || mirrorCfg.y || mirrorCfg.z);
  const mirrorCenter: [number, number, number] = mirrorOn
    ? [mirrorCfg.sumX / 2, mirrorCfg.sumY / 2, mirrorCfg.sumZ / 2]
    : [0, 0, 0];
  const mirrorRefSign: [number, number, number] = mirrorOn
    ? [
        Math.sign(pick.ix - mirrorCenter[0]),
        Math.sign(pick.iy - mirrorCenter[1]),
        Math.sign(pick.iz - mirrorCenter[2]),
      ]
    : [0, 0, 0];

  const state = setInteraction({
    kind: "move_selection",
    controllerIndex,
    originals,
    originalsBBox,
    grabDistance: pick.distance,
    startGrabPosGrid,
    currentOffset: { dx: 0, dy: 0, dz: 0 },
    displaced: new Map(),
    screenDriven,
    faceNormalAxis,
    mirror: mirrorOn
      ? { x: mirrorCfg.x, y: mirrorCfg.y, z: mirrorCfg.z }
      : null,
    mirrorRefSign,
    mirrorCenter,
  });

  // Place at offset (0,0,0) — back at originals — so the immediate
  // visual state matches pre-press if the user releases right away.
  placeAtOffset(state, grid);
  return true;
}

export function handleMoveDrag(
  grid: VoxelGrid,
  gridGroup: THREE.Object3D,
  bounds: CellBounds,
  selection: Selection,
): void {
  if (interaction.kind !== "move_selection") return;
  const state = interaction;
  if (!controllerStates[state.controllerIndex].selecting) return;
  // Desktop drives the offset from a horizontal floor-drag in main —
  // the laser endpoint sweeps a camera-centered sphere there, which maps
  // poorly to a mouse, so we feed setMoveOffset directly instead.
  if (state.screenDriven) return;

  laserEndpointInGridLocal(
    state.controllerIndex,
    state.grabDistance,
    gridGroup,
    _scratchEndpointGrid,
  );
  setMoveOffset(
    (_scratchEndpointGrid.x - state.startGrabPosGrid.x) / CELL_SIZE,
    (_scratchEndpointGrid.y - state.startGrabPosGrid.y) / CELL_SIZE,
    (_scratchEndpointGrid.z - state.startGrabPosGrid.z) / CELL_SIZE,
    grid,
    bounds,
    selection,
  );
}

// Apply a float (dx, dy, dz) cell offset to the active move: snap each
// axis with hysteresis, clamp to the work volume, and re-place the cells
// if the integer offset changed. Shared by the VR laser drag and the
// desktop floor-drag.
//
// Pre-round with hysteresis so a hand/cursor parked at a wall doesn't
// flip the offset by ±1 every few frames around the rounding boundary;
// the deadband holds the previous integer until the raw clearly crosses.
export function setMoveOffset(
  dxFloat: number,
  dyFloat: number,
  dzFloat: number,
  grid: VoxelGrid,
  bounds: CellBounds,
  selection: Selection,
): void {
  if (interaction.kind !== "move_selection") return;
  const state = interaction;
  const dxRaw = snapWithHysteresis(dxFloat, state.currentOffset.dx);
  const dyRaw = snapWithHysteresis(dyFloat, state.currentOffset.dy);
  const dzRaw = snapWithHysteresis(dzFloat, state.currentOffset.dz);

  // Per-axis clamp against the work-volume walls so a drag past the
  // wall slides along it instead of pushing cells out of bounds. Each
  // axis is independent — drag right + up hitting the right wall still
  // permits the up component. Mirror axes clamp symmetrically (both halves
  // move, so the offset is bounded by the tighter of the two wall margins).
  const bb = state.originalsBBox;
  const m = state.mirror;
  const [xlo, xhi] = m?.x
    ? mirrorClampRange(state, 0, bounds.minIx, bounds.maxIx)
    : [bounds.minIx - bb.minIx, bounds.maxIx - 1 - bb.maxIx];
  const [ylo, yhi] = m?.y
    ? mirrorClampRange(state, 1, bounds.minIy, bounds.maxIy)
    : [bounds.minIy - bb.minIy, bounds.maxIy - 1 - bb.maxIy];
  const [zlo, zhi] = m?.z
    ? mirrorClampRange(state, 2, bounds.minIz, bounds.maxIz)
    : [bounds.minIz - bb.minIz, bounds.maxIz - 1 - bb.maxIz];
  const dx = clamp(dxRaw, xlo, xhi);
  const dy = clamp(dyRaw, ylo, yhi);
  const dz = clamp(dzRaw, zlo, zhi);

  if (
    dx === state.currentOffset.dx &&
    dy === state.currentOffset.dy &&
    dz === state.currentOffset.dz
  ) {
    return;
  }

  unplaceAtOffset(state, grid);
  state.currentOffset = { dx, dy, dz };
  placeAtOffset(state, grid);
  // Keep the selection (and thus its highlight) on the cells as they move.
  selection.replace(movedCells(state));
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

// Allowed offset range [lo, hi] for a mirror axis. The grabbed half moves +d and
// the other half −d, so each half must stay in bounds INDEPENDENTLY — using the
// combined bbox would wrongly lock the offset once either edge touches a wall,
// even though moving the halves INWARD (toward each other) never approaches a
// wall. So we bound +d by the grabbed-side cells' room and −d by the opposite
// side's room, separately. A grab on the mirror plane (refSign 0) locks the axis.
function mirrorClampRange(
  state: MoveState,
  axis: 0 | 1 | 2,
  boundMin: number,
  boundMax: number,
): [number, number] {
  const refSign = state.mirrorRefSign[axis];
  if (refSign === 0) return [0, 0];
  const center = state.mirrorCenter[axis];
  // Split into cells that move +d (same side as the grab) vs −d (far side).
  let posMin = Infinity;
  let posMax = -Infinity;
  let negMin = Infinity;
  let negMax = -Infinity;
  for (const o of state.originals) {
    const coord = axis === 0 ? o.ix : axis === 1 ? o.iy : o.iz;
    const s = Math.sign(coord - center) * refSign; // +1 grabbed side, −1 far, 0 plane
    if (s > 0) {
      if (coord < posMin) posMin = coord;
      if (coord > posMax) posMax = coord;
    } else if (s < 0) {
      if (coord < negMin) negMin = coord;
      if (coord > negMax) negMax = coord;
    }
  }
  const hiBound = boundMax - 1;
  let lo = -Infinity;
  let hi = Infinity;
  if (posMax > -Infinity) {
    hi = Math.min(hi, hiBound - posMax); // +d cells can't pass the top wall
    lo = Math.max(lo, boundMin - posMin); // …or the bottom wall
  }
  if (negMax > -Infinity) {
    hi = Math.min(hi, negMin - boundMin); // −d cells can't pass the bottom wall
    lo = Math.max(lo, negMax - hiBound); // …or the top wall
  }
  return lo <= hi ? [lo, hi] : [0, 0];
}

// One cell's effective offset. Plain `currentOffset` for a rigid move; for a
// mirror move each enabled axis is reflected by the cell's side relative to the
// grabbed half (so the two halves move oppositely; plane cells don't move).
function cellOffset(
  state: MoveState,
  o: { ix: number; iy: number; iz: number },
): [number, number, number] {
  const { dx, dy, dz } = state.currentOffset;
  const m = state.mirror;
  if (!m) return [dx, dy, dz];
  const rs = state.mirrorRefSign;
  const c = state.mirrorCenter;
  return [
    m.x ? dx * rs[0] * Math.sign(o.ix - c[0]) : dx,
    m.y ? dy * rs[1] * Math.sign(o.iy - c[1]) : dy,
    m.z ? dz * rs[2] * Math.sign(o.iz - c[2]) : dz,
  ];
}

// Round `raw` to an integer, but only leave `prev` when `raw` is at
// least HYSTERESIS_DEADBAND past the normal half-cell rounding boundary.
// Symmetric in both directions, so a slow drag still ratchets through
// integers at roughly the same pace — just with a small dead zone that
// kills sub-cell jitter when the hand is parked.
const HYSTERESIS_DEADBAND = 0.15;
function snapWithHysteresis(raw: number, prev: number): number {
  if (raw >= prev + 0.5 + HYSTERESIS_DEADBAND) return Math.round(raw);
  if (raw <= prev - 0.5 - HYSTERESIS_DEADBAND) return Math.round(raw);
  return prev;
}

// The selection's cells at the current move offset (originals + per-cell offset).
function movedCells(state: MoveState): Array<{ ix: number; iy: number; iz: number }> {
  return state.originals.map((o) => {
    const [dx, dy, dz] = cellOffset(state, o);
    return { ix: o.ix + dx, iy: o.iy + dy, iz: o.iz + dz };
  });
}

// Update the Selection to point at the new positions. Grid is already
// in its post-move state — no further mutation here. (setMoveOffset already
// keeps it in sync during the drag; this locks in the final spot on release.)
export function finalizeMoveSelection(selection: Selection): void {
  if (interaction.kind !== "move_selection") return;
  selection.replace(movedCells(interaction));
}

function placeAtOffset(state: MoveState, grid: VoxelGrid): void {
  for (const o of state.originals) {
    const [dx, dy, dz] = cellOffset(state, o);
    const tx = o.ix + dx;
    const ty = o.iy + dy;
    const tz = o.iz + dz;
    if (grid.has(tx, ty, tz)) {
      state.displaced.set(`${tx},${ty},${tz}`, grid.get(tx, ty, tz));
    }
    grid.set(tx, ty, tz, o.palette);
  }
}

function unplaceAtOffset(state: MoveState, grid: VoxelGrid): void {
  for (const o of state.originals) {
    const [dx, dy, dz] = cellOffset(state, o);
    grid.delete(o.ix + dx, o.iy + dy, o.iz + dz);
  }
  for (const [key, palette] of state.displaced) {
    const parts = key.split(",");
    grid.set(Number(parts[0]), Number(parts[1]), Number(parts[2]), palette);
  }
  state.displaced.clear();
}

